<script setup lang="ts">
import { cloneDeep } from "lodash";
import { computed, ref, toRefs } from "vue";
import { useToast } from "vue-toast-notification";
import JSONtoHTML from "@/components/forms/SharedComponents/WYSIWYG/JSONtoHTML.vue";
import { formatDate } from "@/components/MyStudents/Submissions/components/SubmissionsInformation/utils";
import { useCommentAttachment } from "@/components/MyStudents/Submissions/composables/useCommentAttachment";
import { useUpsertTodoComment } from "@/components/MyStudents/Submissions/composables/useUpsertTodoComment";
import EditIcon from "@/components/shared/icons/EditIcon.vue";
import TrashOutlineIcon from "@/components/shared/icons/TrashOutlineIcon.vue";
import { ERROR_TIMEOUT } from "@/constants.js";
import { FILE_SIZE_HINT } from "../constants";
import type { CommentItemProps, TiptapDoc, TodoComment } from "../types";
import { getAttachmentName, getInitials, hasCommentBody, isCommentEdited, toCommentBodyPayload } from "../utils";
import AttachButton from "./AttachButton.vue";
import AttachmentChip from "./AttachmentChip.vue";
import AttachmentError from "./AttachmentError.vue";
import CommentEditor from "./CommentEditor.vue";

const props = defineProps<CommentItemProps>();
const { comment } = toRefs(props);

const emit = defineEmits<(e: "delete", comment: TodoComment) => void>();

const toast = useToast();

const isEditing = ref<boolean>(false);
const draftBody = ref<TiptapDoc | undefined>();
const { attachment, error, isUploading, selectFiles, removeAttachment, reset, resolveFileUpload } =
  useCommentAttachment();
const { mutateAsync: upsertComment, isPending } = useUpsertTodoComment();

const authorName = computed<string>(() => comment.value.author?.full_name ?? "");
const initials = computed<string>(() => getInitials(authorName.value));
const createdAt = computed<string>(() => formatDate(comment.value.created_at));
const isEdited = computed<boolean>(() => isCommentEdited(comment.value));
const isSubmitting = computed<boolean>(() => isUploading.value || isPending.value);
const canUpdate = computed<boolean>(
  () => (hasCommentBody(draftBody.value) || !!attachment.value) && !isSubmitting.value,
);

const startEditing = (): void => {
  draftBody.value = cloneDeep(comment.value.comment_body);
  reset(comment.value.file_upload);
  isEditing.value = true;
};

const cancelEditing = (): void => {
  isEditing.value = false;
};

const updateComment = async (): Promise<void> => {
  try {
    const fileUpload = await resolveFileUpload();
    await upsertComment({
      applicationId: comment.value.application_id,
      todoId: comment.value.todo_id,
      payload: {
        id: comment.value.id,
        comment_body: toCommentBodyPayload(draftBody.value),
        file_upload: fileUpload ?? null,
      },
    });
    isEditing.value = false;
  } catch {
    toast.open({
      message: "Error while updating the comment. Please, try it later.",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  }
};
</script>

<template>
  <div class="flex gap-3" data-testid="internal-comment-item">
    <span
      class="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-full bg-teal-900 text-13p font-bold tracking-[0.3px] text-white"
      data-testid="internal-comment-avatar"
    >
      {{ initials }}
    </span>
    <div class="flex-1 min-w-0">
      <div class="flex flex-wrap items-baseline gap-2">
        <span class="text-13.5p font-bold text-indigo-base" data-testid="internal-comment-author">{{ authorName }}</span>
        <span class="text-xs font-medium text-gray-600" data-testid="internal-comment-date">{{ createdAt }}</span>
        <span v-if="isEdited" class="text-xs font-medium italic text-gray-500" data-testid="internal-comment-edited">
          (edited)
        </span>
      </div>

      <div v-if="isEditing" class="mt-2" data-testid="internal-comment-edit">
        <CommentEditor v-model="draftBody" compact />
        <div class="flex flex-wrap items-center gap-2.5 mt-2.5">
          <AttachButton :disabled="!!attachment" @select="selectFiles" />
          <span class="shrink-0 whitespace-nowrap text-11.5p font-medium text-gray-600">{{ FILE_SIZE_HINT }}</span>
          <div v-if="attachment" class="flex min-w-0 max-w-full">
            <AttachmentChip :name="attachment.name" removable @remove="removeAttachment" />
          </div>
          <AttachmentError :message="error" class="basis-full" />
        </div>
        <div class="flex gap-2 mt-2.5">
          <button
            type="button"
            class="h-[34px] px-4 rounded border border-teal-900 bg-teal-900 text-xs font-bold uppercase tracking-[0.5px] text-white transition-colors duration-100 hover:bg-[#006667] disabled:cursor-not-allowed disabled:opacity-70"
            :disabled="!canUpdate"
            data-testid="internal-comment-update-button"
            @click="updateComment"
          >
            {{ isSubmitting ? "Updating…" : "Update" }}
          </button>
          <button
            type="button"
            class="h-[34px] px-4 rounded border border-gray-475 bg-transparent text-xs font-bold uppercase tracking-[0.5px] text-indigo-base transition-colors duration-100 hover:bg-gray-125 disabled:cursor-not-allowed disabled:opacity-70"
            :disabled="isSubmitting"
            data-testid="internal-comment-cancel-button"
            @click="cancelEditing"
          >
            Cancel
          </button>
        </div>
      </div>

      <template v-else>
        <JSONtoHTML
          v-if="hasCommentBody(comment.comment_body)"
          :tiptap-json="comment.comment_body"
          custom-class="mt-1 text-sm leading-[1.55] text-indigo-base [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:my-1"
          data-testid="internal-comment-body"
        />
        <div v-if="comment.file_upload" class="flex flex-wrap gap-2 mt-2">
          <AttachmentChip
            :name="getAttachmentName(comment.file_upload)"
            :size="comment.file_upload.size"
          />
        </div>
        <div v-if="canManage" class="flex gap-3.5 mt-2" data-testid="internal-comment-actions">
          <button
            type="button"
            class="inline-flex items-center gap-1 text-12.5p font-bold tracking-[0.2px] text-teal-900 hover:underline"
            data-testid="internal-comment-edit-button"
            @click="startEditing"
          >
            <EditIcon class="w-3 h-3 -translate-y-px" />
            Edit
          </button>
          <button
            type="button"
            class="inline-flex items-center gap-1 text-12.5p font-bold tracking-[0.2px] text-red-700 hover:underline"
            data-testid="internal-comment-delete-button"
            @click="emit('delete', comment)"
          >
            <TrashOutlineIcon class="w-3 h-3 -translate-y-px" />
            Delete
          </button>
        </div>
      </template>
    </div>
  </div>
</template>
